import { StoriesType } from '@app/types/modules/stories';
import { VideoPageSettingsType } from '../../types';
import { TeamType } from '@app/types/modules/teams';
import { InquiryFormTemplateInstanceType } from '@app/redux/inquiryFormTemplates/types';
import { StorytellersSelectType } from '@app/containers/CampaignCreationV2/types';
import { CTA } from '@app/redux/cta/types';
import { WorkspacePlansEnum } from '@app/constants/modules/workspace';
import type { DeliveryChannel } from '@app/constants/modules/campaign';
import type { CampaignEntitlement } from '@app/components/modules/funnel/types';
import { RuleType } from '@app/types/modules/audience';
import { PrebuiltCampaignType } from '@app/types/modules/prebuiltCampaign';

export enum CampaignStateEnum {
	active = 'active',
	inactive = 'inactive',
	draft = 'draft',
}

export type campaignStatusType =
	| CampaignStateEnum.active
	| CampaignStateEnum.inactive
	| CampaignStateEnum.draft;

export enum SyncStatus {
	READY = 'ready',
	INACTIVE = 'inactive',
	IMPORTING = 'importing',
	ACTIVE = 'active',
}

export type CampaignExpirationDataType = {
	expirationDate?: string | null;
	expirationTimezone?: string;
	storytellers?: StorytellersSelectType[];
};

export type StatsType = {
	playPercentage: number;
	engagementRate: number;
	numberOfPlays: number;
	uniquePlays: number;
	storyViews: number;
	uniqueStoryViews: number;
	shares: number;
	shareViews: number;
	shareSignUps: number;
	totalTransactedSum: number;
	numberOfLinkClicks: number;
	storiesSent: number;
};

export type CampaignSettingsType = VideoPageSettingsType;

export type ContactsByPreferenceType = {
	both: number;
	email: number;
	sms: number;
};

export type CampaignOverlayType = {
	_id: string;
	name: string;
	snapGroupdId: string;
	snapLensId: string;
	[key: string]: any;
};

export type CampaignType = {
	lastSetLiveAt: string;
	audienceHandlingType: string;
	audienceSource: string;
	value: any;
	hideReply?: boolean;
	tags: any[];
	contacts: any[];
	contactsAdded: number;
	contactUniqueIdentifier: string;
	team?: string | TeamType;
	storytellers: any[];
	completedStories: any[];
	campaignFallback?: boolean;
	contactsByPreference: ContactsByPreferenceType;
	stats: StatsType;
	callToActions: CTA[];
	pinned?: boolean;
	audience: any;
	audienceRules?: RuleType[];
	state: campaignStatusType;
	creationMethod: string;
	displayMode: string;
	expirationMethod: string;
	personalization: string;
	reelsTitle?: string;
	pendingStories: any[];
	assignedStories: any[];
	storiesPendingApproval: any[];
	videosRequiringApproval: number;
	storiesCount: number;
	instructions?: string;
	dueDate?: string;
	noInsights: number;
	waitForProcessing: number;
	strictApproval: number | boolean;
	heartCount: number;
	sendThroughDefault: boolean;
	childCampaigns: any[];
	isChild: boolean;
	smsSent: boolean;
	unsubscribes: number;
	bounces: number;
	emailPreferenceCount: number;
	SMSPreferenceCount: number;
	bothPreferenceCount: number;
	isComplete: boolean;
	scheduledStories: any[];
	selectedContactLists: any[];
	scheduledByWindow: boolean;
	scheduledForNextWindowOpening: boolean;
	_id: string;
	name: string;
	location: string;
	scheduleDate: string | Date;
	timezone?: string;
	smsBody?: string;
	emailSubjectTitle?: string;
	recipe?: any;
	workspace: string;
	lastUpdatedBy: string;
	createdBy: {
		_id: string;
		id: string;
		fullName: string;
		firstName: string;
		lastName: string;
	};
	launchedBy: {
		_id: string;
		id: string;
		fullName: string;
		firstName: string;
		lastName: string;
	};
	triggers: any[];
	createdAt: string | Date;
	updatedAt: string | Date;
	currentChild: null | any[];
	currentStep: string;
	filter: null | string;
	video: any;
	template: null | any;
	launchedAt: string | Date;
	emailsSent: number;
	lastStorySentAt: string;
	lastStorySentUrl: string;
	emailOpens: number;
	engagementCount: number;
	lastStoryResentAt: string;
	contactCount: number;
	assignedStoriesCount: number;
	completedStoriesCount: number;
	pendingStoriesCount: number;
	storiesPendingApprovalCount: number;
	id: string;
	formattedUpdatedAt: string;
	formattedCreatedAt: string;
	recordingScript: string;
	autoplay: boolean;
	settings?: CampaignSettingsType;
	syncStatus?: SyncStatus;
	lastSyncDate?: string | Date;
	subscriberListId?: string;
	emailTemplateId?: string;
	assignmentType?: string;
	overlay?: CampaignOverlayType;
	widgetDomain?: string;
	widgetPath?: string;
	whatsappTemplateId?: string;
	videoViewer?: string;
	lastImportJob?: any;
	whatsappEnabled?: boolean;
	replyToName?: string;
	replyToAddress?: string;
	emailSenderName?: string;
	emailSendingAddress?: string;
	message?: {
		campaign?: string;
		content: {
			text: string;
			id: string;
			medias: {
				status: string;
				type: string;
				url: string;
				_id: string;
			}[];
		};
		whatsappTemplateId?: string;
		updatedAt: string;
	};
	lastMessageSent?: {
		campaign: string;
		_id: string;
		updatedAt: string;
	};
	groups?: any;
	automation?: {
		//Saved automation data
		createdAt?: string;
		deleted?: boolean;
		events?: any;
		fromActiveCampaigns?: boolean;
		id?: string;
		location?: string;
		mappedFields?: any;
		metadata: { targetAssignmentHours: number };
		state: string;
		targetCampaign: {
			id: string;
			_id: string;
			name: string;
		};
		updatedAt: string;
		workspace: string;
		__v: number;
		_id: string;
	};
	scheduledMessages?: { campaign: string; id: string; _id: string }[];
	/**
	 * @deprecated Use `deliveryChannels` instead. Retained only for
	 * backward-compatibility with campaigns persisted before the
	 * `deliveryChannel` -> `deliveryChannels` migration. Read access should go
	 * through `resolveDeliveryChannels`/`has*Channel` helpers in
	 * `@app/utils/modules/campaigns`.
	 */
	deliveryChannel?: 'all' | 'email' | 'sms' | 'niche_inbox' | null;
	/**
	 * New multi-channel field. An array of the channels this campaign will be
	 * delivered through. Supersedes `deliveryChannel`. An empty/undefined value
	 * means "no explicit preference" and consumers should fall back to
	 * contact-preference-driven defaults.
	 */
	deliveryChannels?: DeliveryChannel[] | null;
	lastStoryWithVideo?: any;
	actionsRequired?: string[];
	isLive?: boolean;
	firstReelStopWithVideo?: Partial<StoriesType>;
	publicEmbedUrl?: string;
	expirationData?: CampaignExpirationDataType;
	publicOpenSubmissionIdentifier?: string;
	publicOpenSubmissionUrl?: string;
	folderName?: string;
	folder?: {
		_id: string;
		name: string;
		size: number;
	};
	publicUrlPermissions?: {
		type: 'public' | 'restricted-domain';
		emailDomains?: string[];
	};
	formInstance: InquiryFormTemplateInstanceType;
	nicheCorePlan?: WorkspacePlansEnum;
	milestone?: string;
	onboardingFormOnly?: boolean;
	nicheInboxMessageContent?: null | {
		text: string;
	};
	entitlement?: CampaignEntitlement | null;
	permanentlyDisabled?: boolean;
	featureId?: string;
	prebuiltSource?: string | PrebuiltCampaignType;
};
